'use client';
import React, { useEffect, useMemo, useState } from 'react';

import '@rainbow-me/rainbowkit/styles.css';

import { darkTheme, RainbowKitProvider } from '@rainbow-me/rainbowkit';
import { WagmiProvider, http, createConfig } from 'wagmi';
import { mainnet, shibarium } from '@wagmi/core/chains';
import type { Chain } from 'viem/chains';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

type WagmiProviderType = {
  children: React.ReactNode;
};

const queryClient = new QueryClient();

// Override chain RPC URLs so wallets (like MetaMask) use correct endpoints when adding networks
// This overrides the incorrect RPC URLs from @wagmi/core/chains
const customShibarium: Chain = {
  ...shibarium,
  rpcUrls: {
    default: {
      http: ['https://rpc.shibarium.shib.io/'],
    },
    public: {
      http: ['https://rpc.shibarium.shib.io/'],
    },
  },
};

// Puppynet (Shibarium Testnet) - Hidden from wagmi provider
// Uncomment and add to chains array if needed in the future
// const customShibariumTestnet: Chain = {
//   ...shibariumTestnet,
//   rpcUrls: {
//     default: {
//       http: ['https://rpc.puppynet.shib.io/'],
//     },
//     public: {
//       http: ['https://rpc.puppynet.shib.io/'],
//     },
//   },
// };

const CustomWagmiProvider = ({ children }: WagmiProviderType) => {
  // Build can SSR client components; some wallet libs try to touch IndexedDB during init.
  // To avoid noisy "indexedDB is not defined" logs during `next build`, we:
  // - keep a minimal, server-safe wagmi config for SSR
  // - lazily load wallet connectors only in the browser
  const serverSafeConfig = useMemo(() => {
    return createConfig({
      chains: [mainnet, customShibarium],
      connectors: [],
      transports: {
        [mainnet.id]: http('https://ethereum-rpc.publicnode.com'),
        [customShibarium.id]: http('https://rpc.shibarium.shib.io'),
      },
      ssr: true,
    });
  }, []);

  const [clientConfig, setClientConfig] = useState<typeof serverSafeConfig | null>(null);

  useEffect(() => {
    let isMounted = true;

    const handleInitClientConfig = async () => {
      // Load connector helpers only on the client to avoid SSR/SSG IndexedDB usage.
      const { connectorsForWallets } = await import('@rainbow-me/rainbowkit');
      const {
        metaMaskWallet,
        trustWallet,
        walletConnectWallet,
        coinbaseWallet,
        rainbowWallet,
      } = await import('@rainbow-me/rainbowkit/wallets');

      const projectId = process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID || '';
      const appName = 'Shib Metaverse';

      const connectors = connectorsForWallets(
        [
          {
            groupName: 'Recommended',
            wallets: [metaMaskWallet, coinbaseWallet, rainbowWallet, walletConnectWallet, trustWallet],
          },
        ],
        { appName, projectId }
      );

      const config = createConfig({
        chains: [mainnet, customShibarium],
        connectors,
        transports: {
          [mainnet.id]: http('https://ethereum-rpc.publicnode.com'),
          [customShibarium.id]: http('https://rpc.shibarium.shib.io'),
        },
        // Client-side config; avoids SSR init paths that can touch IndexedDB
        ssr: false,
      });

      if (isMounted) setClientConfig(config);
    };

    handleInitClientConfig();
    return () => {
      isMounted = false;
    };
  }, []);

  const activeConfig = clientConfig ?? serverSafeConfig;

  return (
    <>
      <WagmiProvider config={activeConfig}>
        <QueryClientProvider client={queryClient}>
          <RainbowKitProvider modalSize="compact" theme={darkTheme()}>
            {children}
          </RainbowKitProvider>
        </QueryClientProvider>
      </WagmiProvider>
    </>
  );
};

export default CustomWagmiProvider;
